import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as ArrowRightIcon } from 'src/assets/svg/arrow-right-line.svg'
import { ReactComponent as TrendUpIcon } from 'src/assets/svg/trend-up.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import { CAMPAIGNS_ROUTE } from 'src/constants/routes'
import styles from './campaignHealthCard.module.scss'

interface CampaignHealthCardProps {
  centerLabel?: string
  hideViewDetails?: boolean
  id: string
  isLifetime?: boolean
  mentions: number
  metricLabel?: string
  name: string
  negativeLabel?: string
  negativePerc: number
  neutralLabel?: string
  neutralPerc: number
  onViewDetails?: () => void
  positiveLabel?: string
  positivePerc: number
  trend: number
  viewDetailsLabel?: string
}

export const CampaignHealthCard: FC<CampaignHealthCardProps> = ({
  centerLabel = 'Mention',
  hideViewDetails = false,
  id,
  isLifetime = false,
  metricLabel = 'Mentions',
  mentions,
  name,
  negativePerc,
  negativeLabel = 'Negative',
  neutralPerc,
  neutralLabel = 'Neutral',
  onViewDetails,
  positivePerc,
  positiveLabel = 'Positive',
  trend,
  viewDetailsLabel = 'View Details',
}) => {
  const navigate = useNavigate()

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails()
      return
    }

    navigate(`${CAMPAIGNS_ROUTE}/${id}?period=lifetime`)
  }
  // SVG donut chart calculations
  const size = 120
  const strokeWidth = 16
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  // Calculate stroke dash arrays for each segment
  const positiveLength = (positivePerc / 100) * circumference
  const negativeLength = (negativePerc / 100) * circumference
  const neutralLength = (neutralPerc / 100) * circumference

  // Calculate offsets (segments start where previous one ends)
  const positiveOffset = 0
  const negativeOffset = -positiveLength
  const neutralOffset = -(positiveLength + negativeLength)

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <TruncatedTooltip className={styles.title} text={name} />
        {!hideViewDetails && (
          <button className={styles.viewDetails} onClick={handleViewDetails}>
            {viewDetailsLabel}
            <ArrowRightIcon className={styles.arrow} />
          </button>
        )}
      </div>

      <div className={styles.content}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Mentions Row */}
          <div className={styles.mentionsRow}>
            <div className={styles.mentionsText}>
              <span className={styles.mentionsLabel}>{metricLabel}:</span>
              <span className={styles.mentionsValue}>
                {mentions.toLocaleString()}
              </span>
            </div>
            {!isLifetime && trend !== 0 && (
              <div
                className={`${styles.trendBadge} ${trend < 0 ? styles.negative : ''}`}
              >
                <TrendUpIcon
                  className={styles.trendIcon}
                  style={
                    trend < 0 ? { transform: 'rotate(180deg)' } : undefined
                  }
                />
                <span className={styles.trendValue}>{Math.abs(trend)}%</span>
              </div>
            )}
          </div>

          {/* Sentiment List */}
          <div className={styles.sentimentList}>
            <div className={styles.sentimentItem}>
              <div className={`${styles.dot} ${styles.positive}`} />
              <span className={styles.percentValue}>{positivePerc}%</span>
              <span className={styles.sentimentLabel}>{positiveLabel}</span>
            </div>
            <div className={styles.sentimentItem}>
              <div className={`${styles.dot} ${styles.neutral}`} />
              <span className={styles.percentValue}>{neutralPerc}%</span>
              <span className={styles.sentimentLabel}>{neutralLabel}</span>
            </div>
            <div className={styles.sentimentItem}>
              <div className={`${styles.dot} ${styles.negative}`} />
              <span className={styles.percentValue}>{negativePerc}%</span>
              <span className={styles.sentimentLabel}>{negativeLabel}</span>
            </div>
          </div>
        </div>

        {/* Right Section - Donut Chart */}
        <div className={styles.rightSection}>
          <div className={styles.donutContainer}>
            <svg
              className={styles.donutSvg}
              height={size}
              viewBox={`0 0 ${size} ${size}`}
              width={size}
            >
              {/* Positive segment (purple) */}
              <circle
                className={styles.donutSegmentPositive}
                cx={size / 2}
                cy={size / 2}
                fill="none"
                r={radius}
                strokeDasharray={`${positiveLength} ${circumference}`}
                strokeDashoffset={positiveOffset}
                strokeWidth={strokeWidth}
              />
              {/* Negative segment (orange) */}
              <circle
                className={styles.donutSegmentNegative}
                cx={size / 2}
                cy={size / 2}
                fill="none"
                r={radius}
                strokeDasharray={`${negativeLength} ${circumference}`}
                strokeDashoffset={negativeOffset}
                strokeWidth={strokeWidth}
              />
              {/* Neutral segment (gray) */}
              <circle
                className={styles.donutSegmentNeutral}
                cx={size / 2}
                cy={size / 2}
                fill="none"
                r={radius}
                strokeDasharray={`${neutralLength} ${circumference}`}
                strokeDashoffset={neutralOffset}
                strokeWidth={strokeWidth}
              />
            </svg>
            <div className={styles.donutCenter}>
              <span className={styles.donutLabel}>{centerLabel}</span>
              <span className={styles.donutValue}>
                {mentions.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
